import { useCallback, useEffect, useRef, useState } from 'react';

/** Dados e erro guardam a carga a que pertencem: de outra carga (outro ciclo), valem como "carregando". */
export type Carga<T> = { estado: 'carregando' } | { estado: 'erro'; chave: string } | { estado: 'pronto'; chave: string; dados: T };

const CARREGANDO = { estado: 'carregando' } as const;

/**
 * Carga de dados identificada por uma chave. A `chave` diz o que carregar (o ciclo do catálogo, os
 * ciclos da coleção de selos, o jogador, o acesso): quando ela muda, a carga recomeça e a resposta da
 * chave anterior é descartada. Sem chave (ainda sem jogador ou ciclo), a carga fica em "carregando".
 * `recarregar` refaz a mesma carga ("Tentar de novo"; no catálogo, também a abertura, VIG-11);
 * `recargas` conta quantas vezes isso aconteceu.
 */
export function useCargaPorChave<T>(chave: string | undefined, carregar: () => Promise<T>) {
  // A função de carga é recriada a cada render; o efeito usa sempre a mais recente, sem depender dela.
  const carregarAtual = useRef(carregar);
  carregarAtual.current = carregar;

  const [carga, setCarga] = useState<Carga<T>>(CARREGANDO);
  const [recargas, setRecargas] = useState(0);

  useEffect(() => {
    if (!chave) return;
    let ativo = true;
    setCarga(CARREGANDO);
    carregarAtual
      .current()
      .then(dados => ativo && setCarga({ estado: 'pronto', chave, dados }))
      .catch(erro => {
        console.error(erro);
        if (ativo) setCarga({ estado: 'erro', chave });
      });
    return () => {
      ativo = false;
    };
  }, [chave, recargas]);

  // No render da troca de ciclo, antes do efeito rodar, a carga ainda é da chave anterior.
  const cargaAtual: Carga<T> = carga.estado !== 'carregando' && carga.chave === chave ? carga : CARREGANDO;

  const recarregar = useCallback(() => setRecargas(n => n + 1), []);

  return { carga: cargaAtual, recarregar, recargas };
}
